import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Tabs, SpaceBetween, Spinner } from "@amzn/awsui-components-react";
import FlexicoUploadComponent from '../FlexiCo/FlexicoUploadComponent';
import FlexicoHistoryComponent from '../FlexiCo/FlexicoHistoryComponent';
import AddOrEditTemplateComponent from '../FlexiCo/AddOrEditTemplateComponent';
import FlexicoAuditLogging from '../FlexiCo/FlexicoAuditLogging';
import FriscoUploadComponent from '../Frisco/FriscoUploadComponent';
import FriscoHistoryComponent from '../Frisco/FriscoHistoryComponent';
import AddOrEditProcessComponent from '../Frisco/AddOrEditProcessComponent';
import FriscoAuditLogging from '../AuditLogging/FriscoAuditLogging';
import LogicoUploadComponent from '../Logico/LogicoUploadComponent';
import LogicoHistoryComponent from '../Logico/LogicoHistoryComponent';
import AddOrEditLogicoProcessComponent from '../Logico/AddOrEditLogicoProcessComponent';
import DatasetRefreshSchedule from '../Cruzer/DatasetRefreshSchedule';
import LogicoAuditLogging from '../Logico/LogicoAuditLogging';
import Bowser from 'bowser';
import { METRIC_TYPE, ACTION_METHODS, SITE_SERVICES } from 'src/katalMetrics/metricsModels';
import { captureMetrics, getServiceForMetricsByPath } from 'src/katalMetrics/metricsHelperMethods';

const ProcessContentRenderer = ({
  activeSubTabId,
  activeProcessTab,
  selectedSidebarItemId,
  id,
  location,
  flexicoTemplates,
  logicoProcesses,
  subProcesses,
  friscoProcessTabs = {},
  onTabChange,
  disableAppLayout = false,
  selectedSidebarContent = null,
  onProcessTabChange
}) => {
  const [isReady, setIsReady] = useState(false);
  const [pageVisitActionPublisher, setPageVisitActionPublisher] = useState();
  const lastProcessedTabRef = useRef(null);
  const isProcessingTabChangeRef = useRef(false);
  const lastCapturedPathRef = useRef('');
  const currentUser = useSelector((state) => state.auth.userId);
  const currentPath = `${location.pathname}${location.search}`;
  const browser = Bowser.getParser(window.navigator.userAgent);
  // Wait for selectedSidebarContent to be ready before rendering
  useEffect(() => {

    if (selectedSidebarItemId) {
      // For 'new' items, we're always ready (no tabs needed)
      if (selectedSidebarItemId === 'new') {
        setIsReady(true);
      } else if (selectedSidebarContent && selectedSidebarContent.length > 0) {
        // If we have sidebar content, we're ready
        setIsReady(true);
      } else {
        // Still waiting for sidebar content
        setIsReady(false);
      }
    } else {
      // No item selected, we're ready to show empty state
      setIsReady(true);
    }

    // Reset the last processed tab when sidebar item changes
    lastProcessedTabRef.current = null;
  }, [selectedSidebarItemId, selectedSidebarContent]);

  // Show loading state only briefly while tabs are being prepared
  // This should rarely be seen now since we set default tabs immediately
  if (!isReady && selectedSidebarItemId && selectedSidebarItemId !== 'new') {
    return (
      <Box padding="l" textAlign="center">
        <Spinner size="large" />
        <Box variant="p" padding={{ top: "s" }}>Loading process details...</Box>
      </Box>
    );
  }
  
  // Helper function to render dynamic tabs
  const renderDynamicTabs = () => {
    if (!selectedSidebarContent || selectedSidebarContent.length === 0) {
      return null;
    }

    // Map activeProcessTab to the correct tab ID format
    const getActiveTabId = () => {
      // Special handling for inline_edit -> edit_in_ui mapping
      if (activeProcessTab === 'inline_edit') {
        return 'edit_in_ui';
      }
      return activeProcessTab;
    };

    return (
      <Box padding={{ bottom: "s" }}>
        <Tabs
          onChange={({ detail }) => {
            // Find the matching sidebar content item and call its action
            // This is the ONLY place where tab actions should be triggered from user clicks
            const tabName = detail.activeTabId.replace(/_/g, ' ').toLowerCase();
            const matchingItem = selectedSidebarContent.find(item =>
              item.label.toLowerCase() === tabName
            );

            if (matchingItem && matchingItem.action) {
              matchingItem.action();
            }
          }}
          activeTabId={getActiveTabId()}
          tabs={selectedSidebarContent.map((item, index) => ({
            label: item.label,
            id: item.label.toLowerCase().replace(/\s+/g, '_'),
            disabled: false
          }))}
          variant="default"
          disableContentPaddings={true}
        />
      </Box>
    );
  };

  // Helper function to render the actual content
  const renderContent = () => {
    let content = null;
    let serviceType;
    
    if (activeSubTabId === 'scheduler') {
      content = (
        <DatasetRefreshSchedule
          key="dataset-refresh-schedule"
        />
      );
      serviceType = SITE_SERVICES.ACCOUNTING_CENTRAL_APP;
    }
    
    // FlexiCo content rendering (Execute tab)
    if (activeSubTabId === 'run' && selectedSidebarItemId &&
      (selectedSidebarItemId === 'new' || flexicoTemplates?.find(t => t.flex_id === selectedSidebarItemId))) {
      
      serviceType = SITE_SERVICES.FLEXICO;

      if (activeProcessTab === 'execute' && selectedSidebarItemId !== 'new') {
        content = (
          <FlexicoUploadComponent
            key={`flexico-${selectedSidebarItemId}-execute`}
            id={id}
            fid={selectedSidebarItemId}
            location={{ search: location.search }}
            disableAppLayout={disableAppLayout}
            selectedSidebarContent={selectedSidebarContent}
          />
        );
      }

      if (activeProcessTab === 'history' && selectedSidebarItemId !== 'new') {
        content = (
          <FlexicoHistoryComponent
            key={`flexico-${selectedSidebarItemId}-history`}
            id={id}
            fid={selectedSidebarItemId}
            location={{ search: location.search }}
            disableAppLayout={disableAppLayout}
            selectedSidebarContent={selectedSidebarContent}
          />
        );
      }

      if (activeProcessTab === 'settings') {
        content = (
          <AddOrEditTemplateComponent
            key={`flexico-${selectedSidebarItemId}-settings`}
            id={id}
            fid={selectedSidebarItemId === 'new' ? undefined : selectedSidebarItemId}
            location={{ search: location.search }}
            disableAppLayout={disableAppLayout}
          />
        );
      }

      if (activeProcessTab === 'audit_logs' && selectedSidebarItemId !== 'new') {
        content = (
          <FlexicoAuditLogging
            key={`flexico-${selectedSidebarItemId}-audit`}
            id={id}
            fid={selectedSidebarItemId}
            disableAppLayout={disableAppLayout}
          />
        );
      }
    }

    // Logico content rendering (Categorize tab)
    if (activeSubTabId === 'categorize' && selectedSidebarItemId &&
      (selectedSidebarItemId === 'new' || logicoProcesses?.find(p => p.logico_process_id === selectedSidebarItemId))) {
      
      serviceType = SITE_SERVICES.LOGICO;

      if (activeProcessTab === 'upload' && selectedSidebarItemId !== 'new') {
        content = (
          <LogicoUploadComponent
            key={`logico-${selectedSidebarItemId}-upload`}
            id={id}
            lpid={selectedSidebarItemId}
            location={{ search: location.search }}
            disableAppLayout={disableAppLayout}
            selectedSidebarContent={selectedSidebarContent}
          />
        );
      }

      if (activeProcessTab === 'history' && selectedSidebarItemId !== 'new') {
        content = (
          <LogicoHistoryComponent
            key={`logico-${selectedSidebarItemId}-history`}
            id={id}
            lpid={selectedSidebarItemId}
            location={{ search: location.search }}
            disableAppLayout={disableAppLayout}
            selectedSidebarContent={selectedSidebarContent}
          />
        );
      }

      if (activeProcessTab === 'settings') {
        content = (
          <AddOrEditLogicoProcessComponent
            key={`logico-${selectedSidebarItemId}-settings`}
            id={id}
            lpid={selectedSidebarItemId === 'new' ? undefined : selectedSidebarItemId}
            location={{ search: location.search }}
            disableAppLayout={disableAppLayout}
          />
        );
      }

      if (activeProcessTab === 'audit_logs' && selectedSidebarItemId !== 'new') {
        content = (
          <LogicoAuditLogging
            key={`logico-${selectedSidebarItemId}-audit`}
            id={id}
            lpid={selectedSidebarItemId}
            disableAppLayout={disableAppLayout}
          />
        );
      }
    }
    
    // Frisco content rendering (Upload tab)
    if (activeSubTabId === 'upload' && selectedSidebarItemId &&
      (selectedSidebarItemId === 'new' || subProcesses?.find(p => p.process_id === selectedSidebarItemId))) {
      
      serviceType = SITE_SERVICES.FRISCO;

      if (activeProcessTab === 'upload' && selectedSidebarItemId !== 'new') {
        content = (
          <FriscoUploadComponent
            key={`frisco-${selectedSidebarItemId}-upload`}
            id={id}
            pid={selectedSidebarItemId}
            location={{ search: location.search }}
            disableAppLayout={disableAppLayout}
            selectedSidebarContent={selectedSidebarContent}
          />
        );
      }

      if (activeProcessTab === 'inline_edit' && selectedSidebarItemId !== 'new') {
        content = (
          <FriscoUploadComponent
            key={`frisco-${selectedSidebarItemId}-inline`}
            id={id}
            pid={selectedSidebarItemId}
            location={{ search: `${location.search}&mode=inline_edit` }}
            disableAppLayout={disableAppLayout}
          />
        );
      }

      if (activeProcessTab === 'history' && selectedSidebarItemId !== 'new') {
        content = (
          <FriscoHistoryComponent
            key={`frisco-${selectedSidebarItemId}-history`}
            id={id}
            pid={selectedSidebarItemId}
            location={{ search: location.search }}
            disableAppLayout={disableAppLayout}
            selectedSidebarContent={selectedSidebarContent}
          />
        );
      }

      if (activeProcessTab === 'settings') {
        content = (
          <AddOrEditProcessComponent
            key={`frisco-${selectedSidebarItemId}-settings`}
            id={id}
            pid={selectedSidebarItemId === 'new' ? undefined : selectedSidebarItemId}
            location={{ search: location.search }}
            disableAppLayout={disableAppLayout}
          />
        );
      }

      if (activeProcessTab === 'audit_logs' && selectedSidebarItemId !== 'new') {
        content = (
          <FriscoAuditLogging
            key={`frisco-${selectedSidebarItemId}-audit`}
            id={id}
            pid={selectedSidebarItemId}
            disableAppLayout={disableAppLayout}
          />
        );
      }
    }

    if (content && lastCapturedPathRef.current != currentPath) {
      console.log('Metrics visited')
      lastCapturedPathRef.current = currentPath;
      const metricConfig = [{
        metricName: 'actionPerformedBy',
        metricValue: currentUser,
        metricType: METRIC_TYPE.STRING,
        isMonitor: true
      },
      {
        metricName: 'visitedPagePath',
        metricValue: currentPath,
        metricType: METRIC_TYPE.STRING,
        isMonitor: true
      },
      {
        metricName: 'browserName',
        metricValue: browser.getBrowserName(),
        metricType: METRIC_TYPE.STRING,
        isMonitor: true
      },
      {
        metricName: 'operatingSystem',
        metricValue: browser.getOSName(),
        metricType: METRIC_TYPE.STRING,
        isMonitor: true
      },
      {
        metricName: 'visitCount',
        metricValue: 1,
        metricType: METRIC_TYPE.COUNTER,
        isMonitor: true
      }];
      captureMetrics(ACTION_METHODS.PAGE_VISIT, metricConfig, serviceType);
    }
    return content;
  };

  // Main render - combine dynamic tabs with content
  return (
    <SpaceBetween size="s">
      {renderDynamicTabs()}
      {renderContent()}
    </SpaceBetween>
  );
};

export default ProcessContentRenderer;
